import {
  ButtonFilterERP,
  ButtonPrimeReact,
  CalendarPrimeReact,
  Checkbox,
  InputCurrency,
  SearchInput,
  TagERP,
} from "@finanedi.es/storybook"
import { faCircleCheck, faClock } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { FacturaRecurrenteByFiltersQueryParamsProps } from "api/FacturaRecurrente.service"
import {
  DOCUMENTO_RECURRENTE_ESTADO,
  DocumentoRecurrenteEstadoType,
} from "api/Types/DocumentoRecurrenteEstado"
import {
  DOCUMENTO_RECURRENTE_INTERVALO,
  DocumentoRecurrenteIntervaloType,
} from "api/Types/DocumentoRecurrenteIntervalo"
import { AnimatePresence } from "framer-motion"
import { Dialog } from "primereact/dialog"
import { Dispatch, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { frontendCurrency } from "utils/formatCurrency"
import { backendDateFormat, frontendDateFormat } from "utils/formatDate"

const DOCUMENTO_RECURRENTE_ESTADO_DEFAULT = {
  [DOCUMENTO_RECURRENTE_ESTADO.EN_CURSO]: false,
  [DOCUMENTO_RECURRENTE_ESTADO.FINALIZADO]: false,
}
const DOCUMENTO_RECURRENTE_INTERVALOS_DEFAULT = {
  [DOCUMENTO_RECURRENTE_INTERVALO.DIARIO]: false,
  [DOCUMENTO_RECURRENTE_INTERVALO.SEMANAL]: false,
  [DOCUMENTO_RECURRENTE_INTERVALO.QUINCENAL]: false,
  [DOCUMENTO_RECURRENTE_INTERVALO.MENSUAL]: false,
  [DOCUMENTO_RECURRENTE_INTERVALO.BIMESTRAL]: false,
  [DOCUMENTO_RECURRENTE_INTERVALO.TRIMESTRAL]: false,
  [DOCUMENTO_RECURRENTE_INTERVALO.SEMESTRAL]: false,
  [DOCUMENTO_RECURRENTE_INTERVALO.ANUAL]: false,
}
export const FilterMenu = ({
  serviceQuery,
  sortingCriteria,
  skipCount,
  maxResultCount,
  resetPagination,
}: {
  serviceQuery: Dispatch<Partial<FacturaRecurrenteByFiltersQueryParamsProps>>
  sortingCriteria?: FacturaRecurrenteByFiltersQueryParamsProps["SortingCriteria"]
  skipCount?: FacturaRecurrenteByFiltersQueryParamsProps["SkipCount"]
  maxResultCount?: FacturaRecurrenteByFiltersQueryParamsProps["MaxResultCount"]
  resetPagination: () => void
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [amount, setAmount] = useState<{
    min: number | undefined
    max: number | undefined
  }>({
    min: undefined,
    max: undefined,
  })
  const [estado, setEstado] = useState(DOCUMENTO_RECURRENTE_ESTADO_DEFAULT)
  const [intervalo, setIntervalo] = useState(
    DOCUMENTO_RECURRENTE_INTERVALOS_DEFAULT
  )
  const [keyword, setKeyword] = useState<string | undefined>()
  const [date, setDate] = useState<Record<string, string | undefined>>()
  const openFilters = () => {
    setMenuOpen(true)
  }
  const queryFixer = (
    queryParams: FacturaRecurrenteByFiltersQueryParamsProps
  ) => {
    const facturasParams = {
      FechaEmisionLowerLimit: queryParams.FechaEmisionLowerLimit,
      FechaEmisionUpperLimit: queryParams.FechaEmisionUpperLimit,
      ImporteLowerLimit: queryParams.ImporteLowerLimit,
      ImporteUpperLimit: queryParams.ImporteUpperLimit,
      Estado: queryParams.Estado,
      Intervalo: queryParams.Intervalo,
      MaxResultCount: queryParams.MaxResultCount,
      SkipCount: queryParams.SkipCount,
      SortingCriteria:
        queryParams.SortingCriteria?.property &&
        queryParams.SortingCriteria?.sort
          ? `${queryParams.SortingCriteria.property} ${queryParams.SortingCriteria.sort}`
          : "",
      RazonSocial: queryParams.RazonSocial,
    }
    const fixedQueryParams = Object.fromEntries(
      Object.entries(facturasParams).filter(([_key, value]) => value) // eslint-disable-line @typescript-eslint/no-unused-vars
    )
    return fixedQueryParams
  }
  const handleQuery = () => {
    serviceQuery(
      queryFixer({
        FechaEmisionLowerLimit:
          date?.min && backendDateFormat(new Date(date.min)),
        FechaEmisionUpperLimit:
          date?.max && backendDateFormat(new Date(date.max)),
        ImporteLowerLimit: amount.min,
        ImporteUpperLimit: amount.max,
        Estado: Object.entries(estado)
          .filter(([_key, value]) => value) // eslint-disable-line @typescript-eslint/no-unused-vars
          .flatMap(
            ([key]) => key
          ) as unknown as DocumentoRecurrenteEstadoType[],
        Intervalo: Object.entries(intervalo)
          .filter(([_key, value]) => value) // eslint-disable-line @typescript-eslint/no-unused-vars
          .flatMap(
            ([key]) => key
          ) as unknown as DocumentoRecurrenteIntervaloType[],
        SortingCriteria: {
          property: sortingCriteria?.property,
          sort: sortingCriteria?.sort,
        },
        MaxResultCount: maxResultCount,
        SkipCount: skipCount,
        RazonSocial: keyword,
      })
    )
  }

  const handleApplyFilters = () => {
    setMenuOpen(false)
  }
  const toggleStatus = (key: DocumentoRecurrenteEstadoType) => {
    setEstado({
      ...estado,
      [key]: !estado?.[key],
    })
  }
  const toggleIntervalo = (key: DocumentoRecurrenteIntervaloType) => {
    setIntervalo({
      ...intervalo,
      [key]: !intervalo?.[key],
    })
  }
  const hideFilter = () => {
    setMenuOpen(!menuOpen)
  }
  const handleResetFilters = () => {
    setAmount({
      min: undefined,
      max: undefined,
    })
    setDate({ min: undefined, max: undefined })
    setEstado(DOCUMENTO_RECURRENTE_ESTADO_DEFAULT)
    setIntervalo(DOCUMENTO_RECURRENTE_INTERVALOS_DEFAULT)
  }
  const INTERVALO_OPTIONS: {
    key: DocumentoRecurrenteIntervaloType
    label: string
  }[] = [
    {
      key: DOCUMENTO_RECURRENTE_INTERVALO.DIARIO,
      label: t("documentos.facturaRec.tab.diaryLabel"),
    },
    {
      key: DOCUMENTO_RECURRENTE_INTERVALO.SEMANAL,
      label: t("documentos.facturaRec.tab.weeklLabel"),
    },
    {
      key: DOCUMENTO_RECURRENTE_INTERVALO.QUINCENAL,
      label: t("documentos.facturaRec.tab.fortnightLabel"),
    },
    {
      key: DOCUMENTO_RECURRENTE_INTERVALO.MENSUAL,
      label: t("documentos.facturaRec.tab.mensualLabel"),
    },
    {
      key: DOCUMENTO_RECURRENTE_INTERVALO.BIMESTRAL,
      label: t("documentos.facturaRec.tab.bimestralLabel"),
    },
    {
      key: DOCUMENTO_RECURRENTE_INTERVALO.TRIMESTRAL,
      label: t("documentos.facturaRec.tab.trimestreLabel"),
    },
    {
      key: DOCUMENTO_RECURRENTE_INTERVALO.SEMESTRAL,
      label: t("documentos.facturaRec.tab.semestreLabel"),
    },
    {
      key: DOCUMENTO_RECURRENTE_INTERVALO.ANUAL,
      label: t("documentos.facturaRec.tab.anualLabel"),
    },
  ]

  const intervaloApplied = INTERVALO_OPTIONS.map((opt) => ({
    label: `${t("documentos.facturaRec.tab.headerInterval")}:`,
    value: intervalo?.[opt.key] ? opt.label : undefined,
    action: () => toggleIntervalo(opt.key),
  }))

  const appliedFiltersTags = [
    {
      label: t("filtros.appliedTag.amountFrom"),
      value: amount?.min ? frontendCurrency(amount?.min) : undefined,
      action: () => setAmount({ ...amount, min: undefined }),
    },
    {
      label: t("filtros.appliedTag.amountTo"),
      value: amount?.max ? frontendCurrency(amount?.max) : undefined,
      action: () => setAmount({ ...amount, max: undefined }),
    },
    {
      label: `${t("filterMenu.appliedTag.statusTag")}`,
      value: estado?.[DOCUMENTO_RECURRENTE_ESTADO.EN_CURSO]
        ? t("documentos.facturaRec.tab.inProgressLabel")
        : undefined,
      action: () => toggleStatus(DOCUMENTO_RECURRENTE_ESTADO.EN_CURSO),
    },
    {
      label: `${t("filterMenu.appliedTag.statusTag")}`,
      value: estado?.[DOCUMENTO_RECURRENTE_ESTADO.FINALIZADO]
        ? t("documentos.facturaRec.tab.finalLabel")
        : undefined,
      action: () => toggleStatus(DOCUMENTO_RECURRENTE_ESTADO.FINALIZADO),
    },
    {
      label: `${t("filtros.appliedTag.dateFrom")}`,
      value: date?.min ? frontendDateFormat(new Date(date.min)) : undefined,
      action: () => setDate({ ...date, min: undefined }),
    },
    {
      label: `${t("filtros.appliedTag.dateTo")}`,
      value: date?.max ? frontendDateFormat(new Date(date.max)) : undefined,
      action: () => setDate({ ...date, max: undefined }),
    },
    ...intervaloApplied,
  ]
  const appliedFilters = appliedFiltersTags.filter((filter) => filter.value)

  useEffect(() => {
    resetPagination?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword, appliedFilters?.length])

  useEffect(() => {
    handleQuery()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    keyword,
    appliedFilters?.length,
    amount?.min,
    amount?.max,
    date?.min,
    date?.max,
    sortingCriteria?.sort,
    sortingCriteria?.property,
    skipCount,
    maxResultCount,
  ])

  return (
    <>
      <ButtonFilterERP
        isActive={appliedFilters.length > 0}
        numberActive={appliedFilters?.length}
        onClick={openFilters}
        deleteFilter={handleResetFilters}
        filterButtonLabel={`${t("filtros.title")}`}
      />
      <SearchInput
        placeholder={`${t("filtros.placeholderSearch")}`}
        onChange={(ev: string) => {
          resetPagination?.()
          setKeyword(ev)
        }}
        inputValue={keyword}
        className='tw-h-10 tw-w-full sm:tw-w-72'
      />
      <AnimatePresence>
        <Dialog
          breakpoints={{ "960px": "75vw" }}
          className='p-dialog-right'
          header={t("filtros.title")}
          visible={menuOpen}
          footer={
            <div className='tw-w-100 tw-border-t-2 tw-pt-6'>
              <ButtonPrimeReact
                className='tw-ml-2 !tw-bg-primary tw-cursor-pointer !tw-w-full'
                onClick={handleApplyFilters}
              >
                {t("filtros.labelApply")}
              </ButtonPrimeReact>
            </div>
          }
          onHide={() => {
            hideFilter()
          }}
          draggable={false}
          resizable={false}
          position='right'
          closable
          dismissableMask
          closeOnEscape
        >
          {!!appliedFilters?.length && (
            <div className='tw-mb-8'>
              <div className='flex tw- tw-mb-4 tw-justify-between'>
                <span className='tw-font-medium'>
                  {t("filterMenu.appliedTag.appliedFilters")}
                </span>
                <span
                  onClick={handleResetFilters}
                  className='tw-text-text-hyperlinks tw-cursor-pointer'
                >
                  {t("filterMenu.appliedTag.deleteAll")}
                </span>
              </div>
              <div className='tw-flex tw-flex-wrap tw-mb-8 tw-gap-2 tw-font-normal tw-text-xs'>
                {appliedFilters.map((filter) => (
                  <div
                    key={`${filter.label}-${filter.value}`}
                    onClick={filter.action}
                    className='tw-items-center tw-justify-between tw-cursor-pointer tw-bg-info-10 tw-text-info tw-rounded-md tw-border tw-border-info tw-py-1 tw-px-2'
                  >
                    <span>
                      {filter.label} {filter.value}
                    </span>
                    <FontAwesomeIcon
                      icon={["fal", "xmark"]}
                      className='tw-ml-2 tw-text-info'
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className='tw-mb-8'>
            <div className='tw-mb-4'>
              <span className='tw-font-medium'>
                {t("filterMenu.invoice.status")}
              </span>
            </div>
            <div
              className='tw-flex tw-items-center tw-mb-4 tw-cursor-pointer'
              onClick={() => toggleStatus(DOCUMENTO_RECURRENTE_ESTADO.EN_CURSO)}
            >
              <Checkbox
                id='en-curso'
                className='tw-mr-4'
                isChecked={estado?.[DOCUMENTO_RECURRENTE_ESTADO.EN_CURSO]}
                onClick={() =>
                  toggleStatus(DOCUMENTO_RECURRENTE_ESTADO.EN_CURSO)
                }
              />
              <TagERP
                value={t("documentos.facturaRec.tab.inProgressLabel")}
                color='blue'
                icon={<FontAwesomeIcon icon={faClock} />}
              />
            </div>
            <div
              className='tw-flex tw-items-center tw-mb-4 tw-cursor-pointer'
              onClick={() =>
                toggleStatus(DOCUMENTO_RECURRENTE_ESTADO.FINALIZADO)
              }
            >
              <Checkbox
                id='finalizado'
                className='tw-mr-4'
                isChecked={estado?.[DOCUMENTO_RECURRENTE_ESTADO.FINALIZADO]}
                onClick={() =>
                  toggleStatus(DOCUMENTO_RECURRENTE_ESTADO.FINALIZADO)
                }
              />
              <TagERP
                value={t("documentos.facturaRec.tab.finalLabel")}
                color='success'
                icon={<FontAwesomeIcon icon={faCircleCheck} />}
              />
            </div>
          </div>

          <div className='tw-mb-8'>
            <div className='tw-mb-4'>
              <span className='tw-font-medium'>{t("filtros.fecha.title")}</span>
            </div>
            <div className='tw-flex tw-flex-row tw-mb-6'>
              <div className='tw-flex tw-flex-col'>
                <label htmlFor='fechaDesde'>
                  {t("filterMenu.invoice.issueDate.since")}
                </label>
                <CalendarPrimeReact
                  id='fechaDesde'
                  name='fechaDesde'
                  clearButtonClassName='!tw-hidden'
                  value={date?.min ? new Date(date.min) : undefined}
                  locale={language}
                  onChange={(e) => {
                    const eventDate = e.target.value as unknown as string
                    setDate({
                      max:
                        date?.max && new Date(eventDate) > new Date(date?.max)
                          ? undefined
                          : date?.max,
                      min: eventDate,
                    })
                  }}
                  placeholder='dd/mm/aaaa'
                  dateFormat='dd/mm/yy'
                  className='tw-w-full'
                  baseZIndex={2302}
                  showIcon
                  maxDate={date?.max ? new Date(date.max) : undefined}
                />
              </div>
              <div className='tw-py-3 tw-px-6 tw-items-baseline tw-mt-auto tw-mb-0'>
                -
              </div>
              <div className='tw-flex tw-flex-col'>
                <label htmlFor='fechaHasta'>
                  {t("filterMenu.invoice.issueDate.to")}
                </label>
                <CalendarPrimeReact
                  id='fechaHasta'
                  name='fechaHasta'
                  clearButtonClassName='!tw-hidden'
                  value={date?.max ? new Date(date.max) : undefined}
                  locale={language}
                  onChange={(e) => {
                    const eventDate = e.target.value as unknown as string
                    setDate({
                      min:
                        date?.min && new Date(eventDate) < new Date(date?.min)
                          ? undefined
                          : date?.min,
                      max: eventDate,
                    })
                  }}
                  placeholder='dd/mm/aaaa'
                  dateFormat='dd/mm/yy'
                  className='tw-w-full'
                  baseZIndex={2302}
                  showIcon
                  minDate={date?.min ? new Date(date.min) : undefined}
                />
              </div>
            </div>
          </div>

          <div className='tw-mb-8'>
            <div className='tw-mb-4'>
              <span className='tw-font-medium'>
                {t("filtros.importe.title")}
              </span>
            </div>
            <div className='tw-flex tw-flex-row tw-mb-8'>
              <div className='tw-flex tw-flex-col'>
                <label htmlFor='amountMin'>
                  {t("filtros.importe.fromLabel")} (€)
                </label>
                <InputCurrency
                  placeholder='0,00'
                  value={amount.min}
                  className='!tw-w-full !tw-h-10 !tw-rounded'
                  suffix='€'
                  min={0}
                  max={amount.max ?? Number.MAX_SAFE_INTEGER}
                  onChange={(value) => setAmount({ ...amount, min: value })}
                />
              </div>
              <div className='tw-py-3 tw-px-6 tw-items-baseline tw-mt-auto tw-mb-0'>
                -
              </div>
              <div className='tw-flex tw-flex-col'>
                <label htmlFor='amountMax'>
                  {t("filtros.importe.toLabel")} (€)
                </label>
                <InputCurrency
                  placeholder='0,00'
                  value={amount.max}
                  className='!tw-w-full !tw-h-10 !tw-rounded'
                  suffix='€'
                  min={amount?.min ?? 0}
                  max={Number.MAX_SAFE_INTEGER}
                  onChange={(value) => setAmount({ ...amount, max: value })}
                />
              </div>
            </div>
          </div>

          <div className='tw-mb-8'>
            <div className='tw-mb-4'>
              <span className='tw-font-medium tw-uppercase'>
                {t("documentos.facturaRec.tab.headerInterval")}
              </span>
            </div>
            <div className='tw-grid tw-grid-cols-2 tw-gap-2'>
              {INTERVALO_OPTIONS.map((opt) => (
                <label
                  key={String(opt.key)}
                  className='tw-flex tw-items-center tw-cursor-pointer'
                  onClick={() => toggleIntervalo(opt.key)}
                >
                  <Checkbox
                    id={`intervalo-${opt.key}`}
                    className='tw-mr-2'
                    isChecked={intervalo?.[opt.key]}
                    onClick={() => toggleIntervalo(opt.key)}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
        </Dialog>
      </AnimatePresence>
    </>
  )
}

export default FilterMenu
